import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { employeesService } from '@/services/apiClient';
import type { CreateEmployeeData } from '@/types/employees';

import { analyticsKeys } from './useAnalytics';
import { userKeys } from './useUsers';

/**
 * Employees Hooks
 * React Query hooks for /api/v1/employees/* endpoints
 */

// Query keys
export const employeeKeys = {
  all: ['employees'] as const,
  profile: (employeeId: string, organizationId?: string) =>
    [...employeeKeys.all, 'profile', employeeId, organizationId ?? null] as const,
};

/**
 * Hook to fetch employee profile (comprehensive profile data)
 *
 * @param organizationId - Super admin only: scope the lookup to a specific
 *   organization. Must be omitted for admins/managers (the backend 403s them
 *   if an organization_id query param is sent).
 */
export const useEmployeeProfile = (
  employeeId: string | undefined,
  organizationId?: string
) => {
  return useQuery({
    queryKey: employeeKeys.profile(employeeId || '', organizationId),
    queryFn: () => employeesService.getEmployeeProfile(employeeId || '', organizationId),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create an employee
 */
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEmployeeData) => employeesService.createEmployee(data),
    onSuccess: () => {
      // Invalidate analytics queries to refetch data
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.employeeAnalytics() });
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.organizationInsights() });
      // Invalidate users queries to refresh the users list
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};
